"use client"

import { Copy } from "@/app/components/copy/copy"
import { getProfileHref } from "@/app/navigation/profile"
import {
  useFormattedAddress,
  useWalletConnect,
} from "@/app/wallet-connect/hooks"
import {
  type UnionAddress,
  getRawAddress,
} from "@anaconda/core/blockchain/address"
import { Trans, useLingui } from "@lingui/react/macro"
import { Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import Link from "next/link"
import { useSelectedOrFeaturedBlockchain } from "../blockchains/filters/use-blockchain-sync"
import { ProfileAvatar } from "../wallet-connect/address"

interface Props {
  address: UnionAddress
  className?: string
  withCopyIcon?: boolean
}

export const WalletAddress = reatomComponent(
  ({ address, className, withCopyIcon = true }: Props) => {
    const { t } = useLingui()
    const rawAddress = getRawAddress(address)
    const formattedAddress = useFormattedAddress(rawAddress)

    return (
      <Copy
        copyValue={rawAddress}
        className={className}
        label={t`Copy address`}
        withIcon={withCopyIcon}
      >
        <LiteralDotsText value={formattedAddress} />
      </Copy>
    )
  },
  "WalletAddress",
)

function LiteralDotsText({ value }: { value: string }) {
  const [start, end] = value.split("...")

  if (end === undefined) {
    return <span>{value}</span>
  }

  return (
    <span className="inline-flex items-baseline">
      <span>{start}</span>
      <span aria-hidden="true" className="inline-flex">
        <span>.</span>
        <span>.</span>
        <span>.</span>
      </span>
      <span>{end}</span>
    </span>
  )
}

interface WalletAddressLinkProps
  extends Omit<React.ComponentProps<typeof Link>, "href" | "children"> {
  address: UnionAddress
  prefetch?: boolean
  withAvatar?: boolean
  avatarSize?: number
}

export const WalletAddressLink = reatomComponent(
  ({
    address,
    prefetch,
    withAvatar = false,
    avatarSize = 32,
    className,
    ...props
  }: WalletAddressLinkProps) => {
    const { selectedBlockchain } = useSelectedOrFeaturedBlockchain()

    return (
      <Link href={getProfileHref(address, selectedBlockchain)} {...props}>
        <WalletPreview
          address={address}
          withAvatar={withAvatar}
          avatarSize={avatarSize}
          className={className}
        />
      </Link>
    )
  },
  "WalletAddressLink",
)

export const WalletPreview = reatomComponent(
  ({
    address,
    withAvatar = false,
    avatarSize = 32,
    className,
  }: {
    address: UnionAddress
    withAvatar?: boolean
    avatarSize?: number
    className?: string
  }) => {
    const { evmUnionAddress, solanaUnionAddress } = useWalletConnect()
    const formattedAddress = useFormattedAddress(getRawAddress(address))
    const normalizedAddress = address.toLowerCase()
    const isCurrentUser =
      evmUnionAddress?.toLowerCase() === normalizedAddress ||
      solanaUnionAddress?.toLowerCase() === normalizedAddress

    return (
      <div className={cn("flex items-center gap-3 text-sm", className)}>
        {withAvatar && (
          <ProfileAvatar imageSize={avatarSize} address={address} />
        )}
        <span>{isCurrentUser ? <Trans>You</Trans> : formattedAddress}</span>
      </div>
    )
  },
  "WalletPreview",
)

export function WalletPreviewSkeleton({
  withAvatar = false,
  avatarSize = 32,
  className,
}: {
  withAvatar?: boolean
  avatarSize?: number
  className?: string
}) {
  return (
    <div className={cn("flex items-center gap-3 text-sm", className)}>
      {withAvatar && (
        <Skeleton
          className={cn(
            `h-[${avatarSize}px] w-[${avatarSize}px]`,
            "rounded-full",
          )}
        />
      )}
      <Skeleton className="h-5 w-[92px]" />
    </div>
  )
}
